/**
 * Overview — the answer to "what is this user's state right now", without
 * having to visit the other tabs. Everything here is read-only.
 */
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { CircleAlert, Info } from "lucide-react";
import {
  findDisagreements,
  providerOrNull,
  type UserAccount,
} from "../../../../../shared/index";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Copyable } from "./copyable";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, Detail, EmptyState } from "./parts";
import { ProviderBadge } from "./provider-badge";
import { formatDate, formatMoney, relativeDate } from "./format";

export function OverviewTab({ account }: { account: UserAccount }) {
  const current = account.emailHistory.find((e) => e.isCurrent);

  // Shares the Billing tab's cache — opening Overview warms it, and the banner
  // appears when it lands rather than holding the page.
  const billing = useQuery({
    queryKey: ["site-user-billing", account.email],
    queryFn: () => api.siteUsers.billing(account.email),
    retry: false,
  });

  const findings = billing.data
    ? findDisagreements(account, billing.data.snapshots)
    : [];

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {findings.map((f) => (
        <div
          key={f.headline}
          className={cn(
            "flex items-start gap-3 rounded-xl border p-4 text-sm lg:col-span-2",
            f.severity === "warning"
              ? "border-warning/50 bg-warning/10"
              : "border-border bg-muted/40"
          )}
        >
          {f.severity === "warning" ? (
            <CircleAlert className="mt-0.5 h-4 w-4 shrink-0" />
          ) : (
            <Info className="mt-0.5 h-4 w-4 shrink-0" />
          )}
          <div>
            <p className="font-medium">{f.headline}</p>
            <p className="text-muted-foreground">{f.detail}</p>
          </div>
        </div>
      ))}

      <Card title="Account">
        <dl className="grid grid-cols-2 gap-4">
          <Detail label="Plan">{account.productPlanName ?? "—"}</Detail>
          <Detail label="Expires">
            {formatDate(account.expiration)}
            {relativeDate(account.expiration) && (
              <span className="ml-2 text-xs font-normal text-muted-foreground">
                {relativeDate(account.expiration)}
              </span>
            )}
          </Detail>
          <Detail label="Registered">
            {formatDate(account.registrationDate)}
          </Detail>
          <Detail label="Origin">{account.origin ?? "—"}</Detail>
          <Detail label="Last charge via">
            {providerOrNull(account.lastChargePaymentProvider) ? (
              <ProviderBadge provider={account.lastChargePaymentProvider} />
            ) : (
              "—"
            )}
          </Detail>
          <Detail label="Next charge via">
            {providerOrNull(account.nextChargePaymentProvider) ? (
              <ProviderBadge provider={account.nextChargePaymentProvider} />
            ) : (
              "—"
            )}
          </Detail>
          <Detail label="Email status">
            {current ? current.status : "—"}
          </Detail>
        </dl>
      </Card>

      <Card
        title="Add-ons"
        description={
          account.addOns.some((a) => a.isActive === false)
            ? `${account.addOns.filter((a) => a.isActive !== false).length} active of ${account.addOns.length}`
            : `${account.addOns.length} on this account`
        }
      >
        {account.addOns.length === 0 ? (
          <EmptyState>No add-ons on this account.</EmptyState>
        ) : (
          <ul className="space-y-2">
            {account.addOns.map((addOn) => (
              <li
                key={`${addOn.name}-${addOn.creationDate}`}
                className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-sm"
              >
                <span className="flex items-center gap-2 font-medium">
                  {addOn.name}
                  {addOn.isActive === false && (
                    <Badge variant="secondary">expired</Badge>
                  )}
                  {addOn.isActive === true && (
                    <Badge variant="success">active</Badge>
                  )}
                </span>
                <span className="text-muted-foreground">
                  since {formatDate(addOn.creationDate)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card
        title="Active subscriptions"
        description="What TipRanks believes the user is paying for"
        className="lg:col-span-2"
      >
        {account.activeSubscriptions.length === 0 ? (
          <EmptyState>No active subscriptions.</EmptyState>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Product</TableHead>
                <TableHead>Provider</TableHead>
                <TableHead>Started</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead>Next charge</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Reason</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {account.activeSubscriptions.map((sub) => (
                <TableRow key={sub.subscriptionId ?? sub.name}>
                  <TableCell className="font-medium">
                    {sub.name}
                    {sub.subscriptionId && (
                      <Copyable
                        value={sub.subscriptionId}
                        mono
                        className="block text-muted-foreground"
                      />
                    )}
                    {sub.frequencyUnit && (
                      <span className="ml-2 text-xs text-muted-foreground">
                        {sub.frequencyLength ?? 1} {sub.frequencyUnit}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    {providerOrNull(sub.paymentProvider) ? (
                      <ProviderBadge provider={sub.paymentProvider} />
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell>{formatDate(sub.startDate)}</TableCell>
                  <TableCell>
                    {formatDate(sub.expiry)}
                    {relativeDate(sub.expiry) && (
                      <span className="block text-xs text-muted-foreground">
                        {relativeDate(sub.expiry)}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>{formatDate(sub.nextChargeDate)}</TableCell>
                  <TableCell>
                    {formatMoney(sub.nextChargeAmount, sub.currency)}
                  </TableCell>
                  <TableCell className="max-w-[16rem] truncate text-muted-foreground">
                    {sub.reason ?? "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
